import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import AuthPage from '@/pages/AuthPage';
import DashboardLayout from '@/components/DashboardLayout';
import Dashboard from '@/pages/Dashboard';
import AIFinancialCoach from '@/pages/AIFinancialCoach';
import SpendingTracker from '@/pages/SpendingTracker';
import SavingsPlanner from '@/pages/SavingsPlanner';
import InflationSimulator from '@/pages/InflationSimulator';
import RetirementPlanner from '@/pages/RetirementPlanner';
import TaxOptimizer from '@/pages/TaxOptimizer';
import SocialSecurity from '@/pages/SocialSecurity';
import RothConversion from '@/pages/RothConversion';
import RMDPlanning from '@/pages/RMDPlanning';
import RevenueStreams from '@/pages/RevenueStreams';
import Reports from '@/pages/Reports';
import NetWorthTracker from '@/pages/NetWorthTracker';
import IncomeSources from '@/pages/IncomeSources';
import BudgetTracker from '@/pages/BudgetTracker';
import WealthHealthScore from '@/pages/WealthHealthScore';
import SharedScorePage from '@/pages/SharedScorePage';
import NotificationSettings from '@/pages/NotificationSettings';
import Household from '@/pages/Household';
import AcceptInvitation from '@/pages/AcceptInvitation';
import ScenarioPlanner from '@/pages/ScenarioPlanner';
import FinancialJourneyTimeline from '@/pages/FinancialJourneyTimeline';
import AdvisorDashboard from '@/pages/AdvisorDashboard';
import AdvisorHouseholdView from '@/pages/AdvisorHouseholdView';
import RetirementGapAnalyzer from '@/pages/RetirementGapAnalyzer';
import GettingStarted from '@/pages/GettingStarted';
import AdminAnalytics from '@/pages/AdminAnalytics';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Profile from '@/pages/Profile';
import FinancialProfilePage from '@/pages/FinancialProfilePage';
import BetaEvaluation from '@/pages/BetaEvaluation';
import PrototypeIndex from '@/pages/prototypes/PrototypeIndex';
import WorkforceParticipantDashboard from '@/pages/prototypes/WorkforceParticipantDashboard';
import WorkforceAssessment from '@/pages/prototypes/WorkforceAssessment';
import WorkforceAdminOutcomes from '@/pages/prototypes/WorkforceAdminOutcomes';
import { Toaster } from 'sonner';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptor
const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Surface backend validation errors as readable strings instead of raw objects.
// Components catching errors can read `error.response?.data?.detail` and get
// a clean message — fixes the "Failed to add income source" UX dead-end.
//
// Also: silent ONE-TIME retry on transient network failures (no response / 502
// / 503 / 504). Per beta-tester report (Feb 2026) the deployed app
// occasionally felt unreachable on first request after idle; a single ~700ms
// silent retry on idempotent verbs eliminates the perceived flakiness without
// hiding genuine errors.
const IDEMPOTENT_RETRY_METHODS = new Set(['get', 'head', 'options', 'put', 'delete']);
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error?.config || {};
    const method = (cfg.method || 'get').toLowerCase();
    const status = error?.response?.status;
    const isNetworkError = !error.response;
    const isTransient5xx = status === 502 || status === 503 || status === 504;
    const shouldRetry =
      (isNetworkError || isTransient5xx) &&
      IDEMPOTENT_RETRY_METHODS.has(method) &&
      !cfg.__retriedOnce;

    if (shouldRetry) {
      cfg.__retriedOnce = true;
      await new Promise((r) => setTimeout(r, 700));
      try {
        return await api.request(cfg);
      } catch (retryErr) {
        // fall through and surface this error to caller below
        error = retryErr;
      }
    }

    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      // FastAPI 422: array of {loc, msg, ...}
      const msgs = detail.map((d) => {
        const field = (d.loc || []).slice(-1)[0];
        return field ? `${field}: ${d.msg}` : d.msg;
      });
      error.response.data.detail = msgs.join('. ');
    }
    return Promise.reject(error);
  }
);

export { api };

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      api.get('/auth/me')
        .then(response => {
          setUser(response.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('auth_token');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Allow child pages to push a fresh user payload into the global App state so
  // the sidebar (and anything else reading `user`) updates without a page reload.
  useEffect(() => {
    const onUserUpdated = (e) => {
      if (e && e.detail) setUser((prev) => ({ ...(prev || {}), ...e.detail }));
    };
    window.addEventListener('wn-user-updated', onUserUpdated);
    return () => window.removeEventListener('wn-user-updated', onUserUpdated);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('auth_token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCFB]">
        <div className="text-lg text-[#4A5568]">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/share/:token" element={<SharedScorePage />} />
          <Route path="/household/accept/:token" element={<AcceptInvitation />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Workforce Navigator™ prototype screens — unauthenticated, for founder review only */}
          <Route path="/prototypes/workforce" element={<PrototypeIndex />} />
          <Route path="/prototypes/workforce/participant-dashboard" element={<WorkforceParticipantDashboard />} />
          <Route path="/prototypes/workforce/assessment" element={<WorkforceAssessment />} />
          <Route path="/prototypes/workforce/admin-outcomes" element={<WorkforceAdminOutcomes />} />
          <Route 
            path="/auth" 
            element={user ? <Navigate to="/" /> : <AuthPage onLogin={handleLogin} />} 
          />
          <Route 
            path="/" 
            element={user ? <DashboardLayout user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          >
            <Route index element={<Dashboard />} />
            <Route path="ai-coach" element={<AIFinancialCoach />} />
            <Route path="wealth-health" element={<WealthHealthScore />} />
            <Route path="scenario-planner" element={<ScenarioPlanner />} />
            <Route path="timeline" element={<FinancialJourneyTimeline />} />
            <Route path="advisor" element={<AdvisorDashboard />} />
            <Route path="advisor/household/:householdId" element={<AdvisorHouseholdView />} />
            <Route path="retirement-gap" element={<RetirementGapAnalyzer />} />
            <Route path="getting-started" element={<GettingStarted />} />
            <Route path="net-worth" element={<NetWorthTracker />} />
            <Route path="income-sources" element={<IncomeSources />} />
            <Route path="budget-tracker" element={<BudgetTracker />} />
            <Route path="household" element={<Household />} />
            <Route path="notifications" element={<NotificationSettings />} />
            <Route path="profile" element={<Profile />} />
            <Route path="financial-profile" element={<FinancialProfilePage />} />
            <Route path="spending-tracker" element={<SpendingTracker />} />
            <Route path="savings-planner" element={<SavingsPlanner />} />
            <Route path="inflation-simulator" element={<InflationSimulator />} />
            <Route path="retirement-planner" element={<RetirementPlanner />} />
            <Route path="tax-optimizer" element={<TaxOptimizer />} />
            <Route path="social-security" element={<SocialSecurity />} />
            <Route path="roth-conversion" element={<RothConversion />} />
            <Route path="rmd-planning" element={<RMDPlanning />} />
            <Route path="revenue-streams" element={<RevenueStreams />} />
            <Route path="reports" element={<Reports />} />
            <Route path="beta-evaluation" element={<BetaEvaluation />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;